import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getErrorMessage } from "@/lib/errors";
import type { AnyValue } from "@/lib/rowTypes";

export interface SimStock {
  id: string;
  ticker: string;
  company_name: string;
  sector: string;
  current_price: number;
  description: string;
  volatility_rating: number;
  trend_direction: string;
}

export interface SimHolding {
  shares: number;
  avgCost: number;
}

export interface RoundSummary {
  round: number;
  netWorth: number;
  cash: number;
  portfolioValue: number;
  hw500: number;
  cycle: string;
  sentiment: number;
  bestPerformer: { ticker: string; change: number };
  worstPerformer: { ticker: string; change: number };
  dividendIncome: number;
  newsCount: number;
}

export interface NewsItem {
  headline: string;
  sector: string | null;
  impact: number;
  tag: string;
  sentiment: string;
}

export interface BehavioralMetrics {
  panic_sells: number;
  hype_buys: number;
  trend_chasing: number;
  overtrading_rounds: number;
  total_buys: number;
  total_sells: number;
  sells_during_fear: number;
  buys_during_greed: number;
  max_concentration_pct: number;
  rounds_undiversified: number;
  biggest_single_loss: number;
  held_through_dip: number;
  trades_per_round: Array<{ round: number; count: number }>;
}

export interface SimulationState {
  id: string;
  persona: string;
  market_personality: string;
  current_round: number;
  max_rounds: number;
  cash_balance: number;
  starting_capital: number;
  market_cycle: string;
  sentiment: number;
  hw500_level: number;
  stock_prices: Record<string, number>;
  holdings: Record<string, SimHolding>;
  price_history: Array<{ round: number; prices: Record<string, number> }>;
  hw500_history: Array<{ round: number; level: number }>;
  news_log: Array<{ round: number; news: NewsItem[] }>;
  round_summaries: RoundSummary[];
  behavioral_metrics: BehavioralMetrics;
  status: string;
}

type SimPhase = "loading" | "setup" | "playing" | "round_summary" | "completed";

export function useSimulator() {
  const [phase, setPhase] = useState<SimPhase>("loading");
  const [simulation, setSimulation] = useState<SimulationState | null>(null);
  const [stocks, setStocks] = useState<SimStock[]>([]);
  const [lastRoundData, setLastRoundData] = useState<AnyValue>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const callEngine = useCallback(async (body: Record<string, AnyValue>) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Not authenticated");

    const { data, error } = await supabase.functions.invoke("simulation-engine", {
      body,
    });
    if (error) throw new Error(error.message || "Request failed");
    if (data?.error) throw new Error(data.error);
    return data;
  }, []);

  const checkExisting = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setPhase("setup"); return; }

    // Check for active simulation
    const { data: existing } = await supabase
      .from("simulation_runs")
      .select("*")
      .eq("user_id", session.user.id)
      .eq("status", "active")
      .order("created_at", { ascending: false })
      .limit(1);

    if (existing && (existing).length > 0) {
      const sim = (existing as AnyValue)[0] as SimulationState;
      setSimulation(sim);

      // Fetch stocks
      const result = await callEngine({ action: "get_state", simulation_id: sim.id });
      if (result.stocks) setStocks(result.stocks);
      setPhase("playing");
    } else {
      setPhase("setup");
    }
  }, [callEngine]);

  const startSimulation = useCallback(async (persona: string, marketPersonality: string) => {
    setActionLoading(true);
    setError(null);
    try {
      const result = await callEngine({
        action: "start",
        persona,
        market_personality: marketPersonality,
      });
      setSimulation(result.simulation);

      // Fetch stocks
      const stateResult = await callEngine({ action: "get_state", simulation_id: result.simulation.id });
      if (stateResult.stocks) setStocks(stateResult.stocks);

      setPhase("playing");
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  }, [callEngine]);

  const buyStock = useCallback(async (ticker: string, shares: number) => {
    if (!simulation) return;
    setActionLoading(true);
    setError(null);
    try {
      const result = await callEngine({
        action: "buy",
        simulation_id: simulation.id,
        ticker,
        shares,
      });
      setSimulation((prev) =>
        prev ? { ...prev, cash_balance: result.cash, holdings: result.holdings, behavioral_metrics: result.behavioral_metrics || prev.behavioral_metrics } : prev
      );
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [simulation, callEngine]);

  const sellStock = useCallback(async (ticker: string, shares: number) => {
    if (!simulation) return;
    setActionLoading(true);
    setError(null);
    try {
      const result = await callEngine({
        action: "sell",
        simulation_id: simulation.id,
        ticker,
        shares,
      });
      setSimulation((prev) =>
        prev ? { ...prev, cash_balance: result.cash, holdings: result.holdings, behavioral_metrics: result.behavioral_metrics || prev.behavioral_metrics } : prev
      );
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [simulation, callEngine]);

  const advanceRound = useCallback(async () => {
    if (!simulation) return;
    setActionLoading(true);
    setError(null);
    try {
      const result = await callEngine({
        action: "advance",
        simulation_id: simulation.id,
      });
      setLastRoundData(result);
      setSimulation((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          current_round: result.round,
          market_cycle: result.cycle,
          sentiment: result.sentiment,
          stock_prices: result.prices,
          hw500_level: result.hw500,
          cash_balance: result.cash,
          holdings: result.holdings,
          round_summaries: [...prev.round_summaries, result.summary],
          price_history: [...prev.price_history, { round: result.round, prices: result.prices }],
          hw500_history: [...prev.hw500_history, { round: result.round, level: result.hw500 }],
          news_log: [...prev.news_log, { round: result.round, news: result.news }],
          behavioral_metrics: result.behavioral_metrics || prev.behavioral_metrics,
          status: result.isComplete ? "completed" : "active",
        };
      });
      if (result.isComplete) {
        setPhase("completed");
      } else {
        setPhase("round_summary");
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  }, [simulation, callEngine]);

  const completeAndTransfer = useCallback(async () => {
    if (!simulation) return null;
    setActionLoading(true);
    try {
      const result = await callEngine({
        action: "complete",
        simulation_id: simulation.id,
      });
      return result;
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      return null;
    } finally {
      setActionLoading(false);
    }
  }, [simulation, callEngine]);

  const dismissRoundSummary = useCallback(() => {
    setPhase("playing");
  }, []);

  // Derived values
  const portfolioValue = simulation
    ? Object.entries(simulation.holdings).reduce((sum, [ticker, h]) => {
        return sum + (simulation.stock_prices[ticker] || 0) * h.shares;
      }, 0)
    : 0;

  const netWorth = simulation ? Number(simulation.cash_balance) + portfolioValue : 0;

  const currentNews = simulation?.news_log?.length
    ? simulation.news_log[simulation.news_log.length - 1]?.news || []
    : [];

  return {
    phase,
    simulation,
    stocks,
    lastRoundData,
    error,
    actionLoading,
    portfolioValue,
    netWorth,
    currentNews,
    checkExisting,
    startSimulation,
    buyStock,
    sellStock,
    advanceRound,
    completeAndTransfer,
    dismissRoundSummary,
  };
}
